/**
 * Centralised TanStack Query keys, namespaced under `['owner', ...]` so
 * cross-section invalidations are easy (`invalidate(['owner','audit'])`
 * refetches both the owner-actions and consent tabs).
 *
 * Add new keys here when wiring up a new endpoint. Keep the shape
 * stable — TanStack Query uses structural equality, not deep equality,
 * so object params should always be serialised the same way.
 */
export const queryKeys = {
  me: () => ['owner', 'me'] as const,
  overview: () => ['owner', 'overview'] as const,

  users: {
    list: (params: Record<string, unknown>) =>
      ['owner', 'users', 'list', params] as const,
    detail: (id: string) => ['owner', 'users', 'detail', id] as const,
    growth: (range: string) => ['owner', 'users', 'growth', range] as const,
    retention: () => ['owner', 'users', 'retention'] as const,
  },

  content: {
    articles: (params: Record<string, unknown>) =>
      ['owner', 'content', 'articles', params] as const,
    tags: () => ['owner', 'content', 'tags'] as const,
    translations: () => ['owner', 'content', 'translations'] as const,
  },

  channels: {
    list: () => ['owner', 'channels', 'list'] as const,
    ingestion: (range: string) =>
      ['owner', 'channels', 'ingestion', range] as const,
    discovery: () => ['owner', 'channels', 'discovery'] as const,
  },

  alerts: () => ['owner', 'alerts'] as const,
  notifications: () => ['owner', 'notifications'] as const,

  mobile: {
    overview: () => ['owner', 'mobile', 'overview'] as const,
    versions: () => ['owner', 'mobile', 'versions'] as const,
    crashes: () => ['owner', 'mobile', 'crashes'] as const,
  },

  analytics: {
    dauMau: (range: string) => ['owner', 'analytics', 'dau-mau', range] as const,
    funnel: (name: string) => ['owner', 'analytics', 'funnel', name] as const,
    events: () => ['owner', 'analytics', 'events'] as const,
  },

  errors: () => ['owner', 'errors'] as const,
  infra: () => ['owner', 'infra'] as const,
  costs: () => ['owner', 'costs'] as const,

  audit: {
    ownerActions: (params: Record<string, unknown>) =>
      ['owner', 'audit', 'owner-actions', params] as const,
    consent: (params: Record<string, unknown>) =>
      ['owner', 'audit', 'consent', params] as const,
  },
} as const;
